import React from "react";

import { connect } from "react-redux";
import { Redirect } from "react-router-dom";
import { useQuery, useMutation, useApolloClient } from '@apollo/client';
import { adopt } from "react-adopt";
import { reduxForm } from "redux-form";

import { destroy } from "redux-form";

import StateMachine from "src/components/utils/StateMachine";
import cloneDeep from "lodash/cloneDeep";

import * as FullStory from '@fullstory/browser';

import {
  UPDATE_USER_GENERAL_INFORMATION,
  UPDATE_USER_SKILLS,
  UPDATE_USER_BRAND_TYPES,
  UPDATE_USER_LOCATIONS,
  UPDATE_USER_PHOTOS,
  SEND_PHONE_NUMBER_VERIFY_CODE,
  CHECK_PHONE_NUMBER_VERIFY_CODE,
  GET_CURRENT_USER,
  SEND_EMAIL_VERIFY_CODE,
  EXCHANGE_TOKEN,
  CHECK_EMAIL_VERIFY_CODE,
  WEB_LOGIN,
  CHECK_EMAIL_VERIFY_CODE_FOR_WEB_LOGIN,
  CHECK_PHONE_NUMBER_VERIFY_CODE_FOR_WEB_LOGIN,
} from "./queries";
import styles from "../../TalentApplication/styles.module.css";
import Spinner from "../../../assets/Spinner/Spinner";

const FORM_NAME = "apply";

const WithClient = ({ render }) => {
  const client = useApolloClient();
  return render(client);
};
const Form = reduxForm({
  form: FORM_NAME,
  destroyOnUnmount: false,
  enableReinitialize: true,
})((props) => <React.Fragment>{props.render(props)}</React.Fragment>);

let WithUserForm = adopt({
  machine: <StateMachine.Consumer />,
  client: ({ render }) => <WithClient render={render} />,
  userQuery: ({ render }) => <UserQuery render={render} />,
  done: ({ render, destroy, ...props }) =>
    render(() => {
      window.localStorage.removeItem("token");
      destroy(FORM_NAME);
      (async function() { await props.client.clearStore()})();
    }),
  updateUserGeneralInformation: ({ render }) => {
    const [updateUserGeneralInformationFunction, { loading, error, data }] = useMutation(UPDATE_USER_GENERAL_INFORMATION);
    return render(updateUserGeneralInformationFunction, { loading, error, data });
  },
  webLogin: ({ render }) => {
    const [webLoginFunction, { loading, error, data }] = useMutation(WEB_LOGIN);
    return render(webLoginFunction, { loading, error, data });
  },
  updateUserSkills: ({ render }) => {
    const [updateUserSkillsFunction, { loading, error, data }] = useMutation(UPDATE_USER_SKILLS);
    return render(updateUserSkillsFunction, { loading, error, data });
  },
  updateUserBrandTypes: ({ render }) => {
    const [updateUserBrandTypesFunction, { loading, error, data }] = useMutation(UPDATE_USER_BRAND_TYPES);
    return render(updateUserBrandTypesFunction, { loading, error, data });
  },
  updateUserLocations: ({ render }) => {
    const [updateUserLocationsFunction, { loading, error, data }] = useMutation(UPDATE_USER_LOCATIONS);
    return render(updateUserLocationsFunction, { loading, error, data });
  },
  updateUserPhotos: ({ render }) => {
    const [updateUserPhotosFunction, { loading, error, data }] = useMutation(UPDATE_USER_PHOTOS);
    return render(updateUserPhotosFunction, { loading, error, data });
  },
  sendEmailVerifyCode: ({ render }) => {
    const [sendEmailVerifyCodeFunction, { loading, error, data }] = useMutation(SEND_EMAIL_VERIFY_CODE);
    return render(sendEmailVerifyCodeFunction, { loading, error, data });
  },
  checkEmailVerifyCode: ({ render }) => {
    const [checkEmailVerifyCodeFunction, { loading, error, data }] = useMutation(CHECK_EMAIL_VERIFY_CODE);
    return render(checkEmailVerifyCodeFunction, { loading, error, data });
  },
  checkEmailVerifyCodeForWebLogin: ({ render }) => {
    const [checkEmailVerifyCodeForWebLoginFunction, { loading, error, data }] = useMutation(CHECK_EMAIL_VERIFY_CODE_FOR_WEB_LOGIN);
    return render(checkEmailVerifyCodeForWebLoginFunction, { loading, error, data });
  },
  sendPhoneNumberVerifyCode: ({ render }) => {
    const [sendPhoneNumberVerifyCodeFunction, { loading, error, data }] = useMutation(SEND_PHONE_NUMBER_VERIFY_CODE);
    return render(sendPhoneNumberVerifyCodeFunction, { loading, error, data });
  },
  checkPhoneNumberVerifyCode: ({ render }) => {
    const [checkPhoneNumberVerifyCodeFunction, { loading, error, data }] = useMutation(CHECK_PHONE_NUMBER_VERIFY_CODE);
    return render(checkPhoneNumberVerifyCodeFunction, { loading, error, data });
  },
  checkPhoneNumberVerifyCodeForWebLogin: ({ render }) => {
    const [checkPhoneNumberVerifyCodeForWebLoginFunction, { loading, error, data }] = useMutation(CHECK_PHONE_NUMBER_VERIFY_CODE_FOR_WEB_LOGIN);
    return render(checkPhoneNumberVerifyCodeForWebLoginFunction, { loading, error, data });
  },
  exchangeToken: ({ render }) => {
    const [exchangeTokenFunction, { loading, error, data }] = useMutation(EXCHANGE_TOKEN);
    return render(exchangeTokenFunction, { loading, error, data });
  },
  form: ({ formRequiresAuthentication = true, ...props }) => {
    const {
      userQuery: { data, loading, error },
    } = props;

    const currentUser = data && data.currentUser;

    if(loading){
      return (
          <div className={styles.Loading}>
            <Spinner color='white' />
          </div>
      )
    }
    const membershipSelectionIsEnabled = process.env.REACT_APP_MEMBERSHIP_SELECTION_ENABLED === 'true';
    let currentPath = window.location.pathname;
    const userIsUnauthenticated = formRequiresAuthentication && !loading && (!localStorage.getItem("token") || !currentUser || error);
    const isOnCompletePageButSignupPending = currentPath === "/apply/complete" && (membershipSelectionIsEnabled ? !currentUser?.membership : (currentUser?.images?.length || 0) < 6);
    const isAfterWebLogin = currentUser?.emailVerificationTime && currentUser?.phoneNumberVerificationTime && currentPath === "/login";
    const isOnMembershipPageWithoutEnoughPhotos = membershipSelectionIsEnabled && currentPath === "/apply/membership" && (currentUser?.images?.length || 0) < 6;
    const hasCompletedSkillsStep = currentUser?.skills?.length > 0 || currentUser?.brandTypes?.length > 0;
    const isOnInstagramStepWithoutSkills = (currentPath === "/apply/instagram" || currentPath === "/apply/photos") && !hasCompletedSkillsStep;
    if(
        userIsUnauthenticated ||
        isOnCompletePageButSignupPending ||
        isAfterWebLogin ||
        isOnMembershipPageWithoutEnoughPhotos ||
        isOnInstagramStepWithoutSkills
    ) {
      return <Redirect to="/apply"/>;
    }
    if(currentUser?.phoneNumberVerificationTime && currentUser?.emailVerificationTime && currentPath !== "/apply/complete" && currentPath !== "/apply/instagram"){
      if (
        (currentUser.skills?.length > 0 || currentUser.brandTypes?.length > 0) && // User selected skills or brand types
        currentUser.instagramToken && // User connected instagram
        currentUser.images?.length >= 6 && // User selected photos
        (currentUser.membership || !membershipSelectionIsEnabled) // User selected membership (if enabled)
      ) {
        // User completed registration:
        // IG token is valid => redirect to /apply/complete
        // IG token not valid => redirect to /apply/instagram
        if (currentUser.instagramTokenRefreshFailedAt){
          // Instagram token already reported not valid
          return (
              <Redirect to="/apply/instagram"/>
          )
        } else {
          // There's a delay before the refresher reports an invalid token.
          // During that time, we validate it using the Graph API.
          // https://graph.instagram.com/v21.0/me/?fields=username&access_token=${currentUser.instagramToken}
          // TODO #596

          // User completed registration, redirect to /apply/complete
          return (
              <Redirect to="/apply/complete"/>
          )
        }
      }
      if (currentPath.endsWith("/") && currentPath !== "/") {
        currentPath = currentPath.slice(0, -1); // Remove last character (trailing slash)
      }
      // User token present (currentUser) we should skip /apply and /apply/verifications
      if (currentPath === "/apply" || currentPath === "/apply/verification") {
        // User connected instagram and selected photos, redirect to membership selection
        if(currentUser.instagramToken && currentUser.images?.length >= 6 && membershipSelectionIsEnabled){
          return <Redirect to="/apply/membership" />;
        }
        // User connected Instagram or selected skills/brand types, redirect to photos selection
        else if (currentUser.instagramToken || currentUser.skills?.length > 0 || currentUser.brandTypes?.length > 0){
          return <Redirect to="/apply/photos" />;
        }
        return <Redirect to="/apply/skills" />;
      }
    }

    return (
      <Form
        initialValues={window.localStorage.getItem("token") && currentUser}
        {...props}
      />
    );
  },
});

WithUserForm = connect(
  void 0,
  { destroy },
)(WithUserForm);

WithUserForm.format = (obj) => {
  //We first create a deep clone of object
  var cloneObj = cloneDeep(obj);
  return WithUserForm.formatObj(cloneObj);
};

WithUserForm.formatUserGeneralInformationInput = (input) => {
  const homeCity = { geonamesId: input.homeCity.geonamesId};
  return {
    id: input.id,
    firstName: input.firstName,
    lastName: input.lastName,
    email: input.email,
    phoneNumber: input.phoneNumber,
    referrer: input.referrer,
    signupInstagramHandle: input.signupInstagramHandle,
    homeCity: homeCity,
    locations: []
  };
};

WithUserForm.formatUserSkillsInput = (input) => {
  return { id: input.id, skills: input.skills };
};

WithUserForm.formatUserBrandTypesInput = (input) => {
  return { id: input.id, brandTypes: input.brandTypes };
};

WithUserForm.formatUserLocationsInput = (input) => {
    const homeCity = { geonamesId: input.homeCity.geonamesId};
    const userInput = {
      id: input.id,
      homeCity: homeCity,
      locations: input.locations || []
    }
    return userInput;
};

WithUserForm.formatUserPhotosInput = (input) => {
  const userInput = {
    id: input.id,
    connectedInstagramHandle: input.connectedInstagramHandle,
    images: input.images
  }
  return userInput;
};

WithUserForm.formatObj = (obj) => {
  // Here we transform a User object into a UserInput object which has less fields.
  for (var property in obj) {
    if (typeof obj[property] === "object" && obj[property] !== null) {
      delete obj.property;
      let newJsonData = WithUserForm.formatObj(obj[property]);
      obj[property] = newJsonData;
    } else {
      if (property === "__typename") {
        delete obj[property];
      }

      ["accepted_tos", "code", "instagramToken", "name"].forEach((field) => {
        delete obj[field];
      });
    }
  }
  return obj;
}

export default WithUserForm;

const UserQuery = ({ render }) => {
  const { data, loading, error, refetch } = useQuery(GET_CURRENT_USER, {
    onCompleted: (data) => {
      if (data.currentUser) {
        FullStory.identify(data.id, data.currentUser);
      }
    }
  });

  const queryResult = {
    data: data ? { currentUser: data.currentUser } : {},
    loading,
    error,
    refetchCurrentUser: refetch
  };

  return render(queryResult);
};